import type { TranslationFunction } from "@src/packages/i18n";
import type { CycleHeaderState } from "../hooks/resolveCycleHeaderState";
import { formatPhaseTime, formatRelativePhaseDate } from "./formatPhaseDate";

export interface HeroCopy {
  title: string;
  summary: string;
  /**
   * Small label above the title (e.g. "Stimulation · day 6") to orient the
   * patient in the cycle. Absent for states without a meaningful phase name
   * (`unknown_active`, `result_recorded`) or when the beta-wait hero owns
   * its own eyebrow ("Two-week wait").
   */
  eyebrow?: { label: string };
}

const HERO_PREFIX = "homepage.review_concept.hero_dynamic";

// Per-state phase label used in the hero eyebrow. Beta states are omitted
// because the two-week-wait hero renders its own eyebrow from wait context.
const EYEBROW_TYPE_KEY: Partial<Record<CycleHeaderState["kind"], string>> = {
  pre_treatment: "pre_treatment",
  stim_trigger_logged: "stimulation",
  stim_monitoring_next: "stimulation",
  stim_med_next: "stimulation",
  stim_generic: "stimulation",
  retrieval_scheduled: "retrieval",
  retrieval_done_pending: "retrieval",
  fertilization: "fertilization",
  embryo_dev: "embryo_development",
  transfer_scheduled: "transfer",
};

function buildEyebrow(
  state: CycleHeaderState,
  t: TranslationFunction,
): HeroCopy["eyebrow"] {
  const typeKey = EYEBROW_TYPE_KEY[state.kind];
  if (!typeKey) return undefined;
  const type = t(`${HERO_PREFIX}.eyebrow_type.${typeKey}`);
  const dayInPhase = "dayInPhase" in state ? state.dayInPhase : null;
  const label = dayInPhase
    ? t(`${HERO_PREFIX}.eyebrow_with_day`, {
        params: { type, day: String(dayInPhase) },
      })
    : t(`${HERO_PREFIX}.eyebrow_no_day`, { params: { type } });
  return { label };
}

/**
 * State-driven hero copy for the homepage. Returns null for states that
 * the static per-variant fallback (`buildHeroStatusCopy`) handles better —
 * `no_cycle` and `cycle_closed` depend on pre/post-cycle scenarios.
 *
 * Copy is educational + body-context (what's happening physically to the
 * user), intentionally distinct from the operational Care page header
 * ("Stim · jour 4") and the Cycle Detail metadata chip.
 */
export function presentCycleHeaderForHero(args: {
  state: CycleHeaderState;
  t: TranslationFunction;
  locale: string | undefined;
  now?: Date;
}): HeroCopy | null {
  const { state, t, locale } = args;
  const now = args.now ?? new Date();
  const base = buildBaseCopy(state, t, locale, now);
  if (!base) return null;
  const eyebrow = buildEyebrow(state, t);
  return eyebrow ? { ...base, eyebrow } : base;
}

function buildBaseCopy(
  state: CycleHeaderState,
  t: TranslationFunction,
  locale: string | undefined,
  now: Date,
): Omit<HeroCopy, "eyebrow"> | null {
  switch (state.kind) {
    case "no_cycle":
    case "cycle_closed":
      return null;
    case "unknown_active":
      return {
        title: t(`${HERO_PREFIX}.unknown_active_title`),
        summary: t(`${HERO_PREFIX}.unknown_active_summary`),
      };
    // Deliberately says nothing about what the result was. The hero is shared
    // across every ending, and naming one here would celebrate a loss.
    case "result_recorded":
      return {
        title: t(`${HERO_PREFIX}.result_recorded_title`),
        summary: t(`${HERO_PREFIX}.result_recorded_summary`),
      };
    case "pre_treatment":
      return {
        title: t(`${HERO_PREFIX}.pre_treatment_title`),
        summary: t(`${HERO_PREFIX}.pre_treatment_summary`),
      };
    case "stim_trigger_logged":
      return {
        title: t(`${HERO_PREFIX}.stim_trigger_logged_title`),
        summary: t(`${HERO_PREFIX}.stim_trigger_logged_summary`),
      };
    case "stim_monitoring_next": {
      const when = formatRelativePhaseDate(state.apptDate, locale, t, { now });
      return {
        title: t(`${HERO_PREFIX}.stim_monitoring_title`, {
          params: { when },
        }),
        summary: t(`${HERO_PREFIX}.stim_monitoring_summary`),
      };
    }
    case "stim_med_next": {
      const when = formatRelativePhaseDate(state.medDate, locale, t, { now });
      const time = formatPhaseTime(state.medTime, locale);
      return {
        title: t(`${HERO_PREFIX}.stim_med_title`, {
          params: { when, time },
        }),
        summary: t(`${HERO_PREFIX}.stim_med_summary`),
      };
    }
    case "stim_generic":
      return {
        title: t(`${HERO_PREFIX}.stim_generic_title`),
        summary: t(`${HERO_PREFIX}.stim_generic_summary`),
      };
    case "retrieval_scheduled": {
      const when = formatRelativePhaseDate(state.retrievalDate, locale, t, {
        now,
      });
      return {
        title: t(`${HERO_PREFIX}.retrieval_scheduled_title`, {
          params: { when },
        }),
        summary: t(`${HERO_PREFIX}.retrieval_scheduled_summary`),
      };
    }
    case "retrieval_done_pending":
      return {
        title: t(`${HERO_PREFIX}.retrieval_done_pending_title`),
        summary: t(`${HERO_PREFIX}.retrieval_done_pending_summary`),
      };
    case "fertilization":
      return {
        title: t(`${HERO_PREFIX}.fertilization_title`),
        summary: t(`${HERO_PREFIX}.fertilization_summary`),
      };
    case "embryo_dev":
      return {
        title: t(`${HERO_PREFIX}.embryo_dev_title`, {
          params: { day: String(state.dayInPhase) },
        }),
        summary: t(`${HERO_PREFIX}.embryo_dev_summary`),
      };
    case "transfer_scheduled": {
      const when = formatRelativePhaseDate(state.transferDate, locale, t, {
        now,
      });
      return {
        title: t(`${HERO_PREFIX}.transfer_scheduled_title`, {
          params: { when },
        }),
        summary: t(`${HERO_PREFIX}.transfer_scheduled_summary`),
      };
    }
    case "beta_scheduled": {
      const when = formatRelativePhaseDate(state.betaDate, locale, t, { now });
      return {
        title: t(`${HERO_PREFIX}.beta_scheduled_title`, {
          params: { when },
        }),
        summary: t(`${HERO_PREFIX}.beta_scheduled_summary`),
      };
    }
    case "beta_pending":
      return {
        title: t(`${HERO_PREFIX}.beta_pending_title`),
        summary: t(`${HERO_PREFIX}.beta_pending_summary`),
      };
  }
}
